import { type Component, markRaw } from 'vue';
import fromEnum from '../io-ts/fromEnum';
import InformationIcon from '../../images/information-bubble.svg?component';
import EvaluationIcon from '../../images/check-circle.svg?component';
import SharingIcon from '../../images/share-2.svg?component';
import AuthorIcon from '../../images/people.svg?component';
import RetentionIcon from '../../images/trash.svg?component';
import ControlIcon from '../../images/controls.svg?component';
import CalendarIcon from '../../images/calendar.svg?component';
import CameraIcon from '../../images/camera.svg?component';
import VideoIcon from '../../images/video.svg?component';
import GearIcon from '../../images/gear.svg?component';
import MotionDetectionIcon from '../../images/motion.svg?component';
import LockIcon from '../../images/lock.svg?component';

export enum SectionType {
    PRIMARY = 'primary',
    INFO = 'info',
    EVALUATION = 'evaluation',
    ROOM_FILTER = 'room_filter',
    USER_FILTER = 'user_filter',
    DATE_FILTER = 'date_filter',
    ROOMS = 'rooms',
    RECORDS = 'records',
    USERS = 'users',
    DATE = 'date',
    GENERAL = 'general',
    SOURCES = 'sources',
    MOTION_DETECTION = 'motion-detection',
    LOCKING = 'locking',
    ASSIGNMENT = 'assignment',
    SHARING = 'sharing',
    AUTHOR = 'author',
    RETENTION = 'retention',
    CONTROL = 'control',
    LINK = 'link',
    OTHER = 'other',
}

export const SectionTypeC = fromEnum('SectionType', SectionType);

export type Section = {
    type: SectionType;
    title: string;
    icon: Component;
}

const createSection = (
    type: SectionType,
    title: string,
    icon: Component,
): Section => ({ type, title, icon });

const InformationSection = createSection(SectionType.INFO, 'Information', markRaw(InformationIcon));
const EvaluationSection = createSection(SectionType.EVALUATION, 'Evaluation', markRaw(EvaluationIcon));
const SharingSection = createSection(SectionType.SHARING, 'Sharing', markRaw(SharingIcon));
const AuthorSection = createSection(SectionType.AUTHOR, 'Author', markRaw(AuthorIcon));
const RetentionSection = createSection(SectionType.RETENTION, 'Retention', markRaw(RetentionIcon));
const ControlSection = createSection(SectionType.CONTROL, 'Control', markRaw(ControlIcon));
const RoomFilterSection = createSection(SectionType.ROOM_FILTER, 'Rooms', markRaw(CameraIcon));
const UserFilterSection = createSection(SectionType.USER_FILTER, 'Users', markRaw(AuthorIcon));
const DateFilterSection = createSection(SectionType.DATE_FILTER, 'Date', markRaw(CalendarIcon));
const RoomsSection = createSection(SectionType.ROOMS, 'Rooms', markRaw(CameraIcon));
const RecordsSection = createSection(SectionType.RECORDS, 'Recordings', markRaw(VideoIcon));
const UserSection = createSection(SectionType.USERS, 'Users', markRaw(AuthorIcon));
const DateSection = createSection(SectionType.DATE, 'Date', markRaw(CalendarIcon));
const PrimarySection = createSection(SectionType.PRIMARY, 'Primary settings', markRaw(GearIcon));
const GeneralSection = createSection(SectionType.GENERAL, 'General info', markRaw(GearIcon));
const SourcesSection = createSection(SectionType.SOURCES, 'Video & audio', markRaw(CameraIcon));
const MotionDetectionSection = createSection(SectionType.MOTION_DETECTION, 'Motion detection', markRaw(MotionDetectionIcon));
const LockingSection = createSection(SectionType.LOCKING, 'Locking room', markRaw(LockIcon));
const AssignmentSection = createSection(SectionType.ASSIGNMENT, 'Assignment', markRaw(AuthorIcon));
const LinkSection = createSection(SectionType.LINK, 'Link', markRaw(LockIcon));
const OtherSection = createSection(SectionType.OTHER, 'Other', markRaw(GearIcon));

const sections: Section[] = [
    PrimarySection,
    GeneralSection,
    InformationSection,
    RoomFilterSection,
    UserFilterSection,
    DateFilterSection,
    RoomsSection,
    RecordsSection,
    UserSection,
    DateSection,
    SourcesSection,
    MotionDetectionSection,
    LockingSection,
    AssignmentSection,
    EvaluationSection,
    SharingSection,
    AuthorSection,
    RetentionSection,
    ControlSection,
    LinkSection,
    OtherSection,
];

export const getSectionByType = (type: SectionType): Section => {
    const session = sections.find((section) => section.type === type);
    if (!session) {
        throw new Error(`Unable to find a section by type "${type}"`);
    }

    return session;
};

export default sections;
